import type { SkillFrontmatter } from '@aline/common';
import { getSkillFrontmatterErrors, parseSkillMarkdown, updateSkillMarkdown } from '@aline/common';
import { isEqual } from 'lodash';
import { isBlankText } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/utils/empty-paragraph';

/** The empty paragraphs the rich-text editor ends a document with, as it serializes them. */
const TRAILING_BLANK_PARAGRAPHS_PATTERN = /\n+[ \t]*[\u200B\uFEFF][\s\u200B\uFEFF]*$/;

/**
 * Helpers for the SKILL.md the editor holds as one string.
 *
 * The whole file, frontmatter included, is what gets saved. The properties block and the
 * body editor each rewrite their half through here, and every rewrite goes through
 * `updateSkillMarkdown`, so keys, quoting and comments nobody edited stay as written.
 */

/** Why this SKILL.md cannot be saved, or null. A missing name or description is not a reason. */
export function getSkillContentError(content: string): string | null {
  const parsed = parseSkillMarkdown(content);
  if (!parsed.ok) return parsed.error;
  return getSkillFrontmatterErrors(parsed.frontmatter).at(0)?.message ?? null;
}

/**
 * Whether two versions of a SKILL.md say the same thing.
 *
 * Compared by meaning rather than by string: the rich-text editor rewrites the body's
 * whitespace and adds an empty paragraph after a trailing heading or table, and
 * re-serializing the frontmatter can change its quoting. None of that should count as an
 * unsaved change on a skill nobody touched.
 */
export function getSkillContentIsEqual(left: string, right: string): boolean {
  const leftParsed = parseSkillMarkdown(left);
  const rightParsed = parseSkillMarkdown(right);
  if (!leftParsed.ok || !rightParsed.ok) return left.trim() === right.trim();
  return (
    getComparableBody(leftParsed.body) === getComparableBody(rightParsed.body) &&
    isEqual(
      getComparableFrontmatter(leftParsed.frontmatter),
      getComparableFrontmatter(rightParsed.frontmatter)
    )
  );
}

/**
 * Whether the editor's SKILL.md matches the saved one. The name is compared as it will be
 * saved: a name input keeps a trailing hyphen while the author is typing, and that alone
 * is not an unsaved change.
 */
export function getSkillContentIsSaved(content: string, savedContent: string): boolean {
  return getSkillContentIsEqual(getSkillContentWithFinalName(content), savedContent);
}

/**
 * The editor's SKILL.md once a save of `submitted` has landed as `saved`. What the author
 * typed stays: it is only replaced when nothing was typed since the save started and the
 * server stored something that means otherwise. Writing the server's copy back
 * unconditionally would, for one, finish the name normalization inside a focused name input.
 */
export function getSkillContentAfterSave({
  current,
  saved,
  submitted,
}: {
  current: string;
  saved: string;
  submitted: string;
}): string {
  if (current !== submitted || getSkillContentIsSaved(current, saved)) return current;
  return saved;
}

/** Replace the body, keeping the frontmatter. A file whose frontmatter does not parse is left alone. */
export function setSkillContentBody(content: string, body: string): string {
  const parsed = parseSkillMarkdown(content);
  if (!parsed.ok) return content;
  return updateSkillMarkdown(content, { body: normalizeEditorMarkdown(body) });
}

/**
 * Apply the fields of `frontmatter` that differ from the file's, keeping the body. A file
 * whose frontmatter does not parse is left alone.
 */
export function setSkillContentFrontmatter(content: string, frontmatter: SkillFrontmatter): string {
  const parsed = parseSkillMarkdown(content);
  if (!parsed.ok) return content;
  const current = parsed.frontmatter;
  const changes = {
    ...(frontmatter.name !== current.name ? { name: frontmatter.name } : {}),
    ...(frontmatter.description !== current.description
      ? { description: frontmatter.description }
      : {}),
    ...(!isEqual(frontmatter.extra, current.extra) ? { extra: frontmatter.extra } : {}),
  };
  if (!Object.keys(changes).length) return content;
  return updateSkillMarkdown(content, changes);
}

/**
 * The SKILL.md as it will be saved. The name inputs keep a trailing hyphen while the author
 * is mid-word ("nda-" on the way to "nda-review"), so a save finishes that normalization.
 * Nothing else about the name is changed: a name typed in the source that breaks the rules
 * is reported, not silently rewritten.
 */
export function getSkillContentWithFinalName(content: string): string {
  const parsed = parseSkillMarkdown(content);
  if (!parsed.ok) return content;
  const name = getFinalSkillName(parsed.frontmatter.name);
  if (name === parsed.frontmatter.name) return content;
  return updateSkillMarkdown(content, { name });
}

/** A name without the trailing hyphen an input keeps while the author types. */
export function getFinalSkillName(name: string): string {
  return name.replace(/-+$/, '');
}

/**
 * Markdown from the rich-text editor as it should be stored.
 *
 * The editor marks an empty paragraph with a zero-width space, which `trim()` keeps. An
 * empty document becomes '', and the empty paragraph the editor appends after a trailing
 * heading, table or code block is dropped, so opening a file never adds an invisible line.
 */
export function normalizeEditorMarkdown(markdown: string): string {
  if (isBlankText(markdown)) return '';
  return markdown.replace(TRAILING_BLANK_PARAGRAPHS_PATTERN, '\n');
}

function getComparableBody(body: string): string {
  return normalizeEditorMarkdown(body).trim();
}

function getComparableFrontmatter({ description, extra, name }: SkillFrontmatter) {
  return { description, extra: Object.entries(extra), name };
}
